import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, ChevronDown, Package, Truck, CheckCircle2, Ban, RotateCcw, Clock, Copy, Check, PackageCheck,
} from "lucide-react";
import type { AppDispatch, RootState } from "../../redux/store";
import { listenToUserOrders, cancelOrder, markOrderDelivered, Order } from "../../redux/ordersSlice";

/* Buyer view of orders: filters, search, expandable cards, a delivery tracker,
   "Mark as received" once the seller has shipped, and "Cancel" while it hasn't.
   Data comes only from ordersSlice (no direct Firebase calls here). */

/* ═══════════════════════════ STYLES: white, black and red (scoped to .co-page) ═══════════════════════════ */
const INK = "#000";
const BODY = "#5C5C5C";
const LINE = "#E5E5E5";
const RED = "#E10000";
const RED_DARK = "#B30000";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');
.co-page, .co-page *, .co-page *::before, .co-page *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.co-page{ position:relative; min-height:100vh; overflow-x:hidden; color:${INK}; background:#fff;
  padding:24px 16px 90px; padding-top:calc(24px + env(safe-area-inset-top)); padding-bottom:calc(90px + env(safe-area-inset-bottom)); }
.co-page h2{ font-family:'Cormorant Garamond',serif; font-size:clamp(36px,6vw,50px); font-weight:500; letter-spacing:.01em; line-height:1.05; margin:14px 0 18px; color:${INK}; }
.co-page p{ margin:0; }
.co-page button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.co-page button:disabled{ cursor:not-allowed; opacity:.5; }
.co-page button:focus-visible, .co-page input:focus-visible{ outline:2px solid ${RED}; outline-offset:2px; }

.co-wrap{ position:relative; z-index:1; width:100%; max-width:720px; margin:0 auto; display:flex; flex-direction:column; gap:14px; }
.co-back{ align-self:flex-start; display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${INK}; padding:6px 2px; transition:color .2s; }
.co-back:hover{ color:${RED}; }
.co-glass{ background:#fff; border:1px solid ${LINE}; border-radius:18px; }
.co-muted{ color:${BODY}; }
.co-error{ background:#fff; color:${RED}; border:1px solid ${RED}; border-radius:12px; padding:11px 14px; font-size:13px; font-weight:500; letter-spacing:.04em; }

.co-btn{ display:inline-flex; align-items:center; justify-content:center; gap:8px; height:42px; padding:0 22px; border-radius:999px;
  background:${INK}; border:1px solid ${INK}; color:#fff !important; font-size:11px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  transition:background .25s, border-color .25s, color .25s, transform .25s; }
.co-btn:hover:not(:disabled){ background:${RED}; border-color:${RED}; transform:translateY(-1px); }
.co-btn:active:not(:disabled){ background:${RED_DARK}; border-color:${RED_DARK}; transform:translateY(0); }
.co-btn.ghost{ background:#fff; color:${INK} !important; border:1px solid ${INK}; }
.co-btn.ghost:hover:not(:disabled){ background:#fff; color:${RED} !important; border-color:${RED}; }
.co-btn.ghost:active:not(:disabled){ background:#fff; color:${RED_DARK} !important; border-color:${RED_DARK}; }

.co-search{ display:flex; align-items:center; gap:10px; padding:0 18px; height:50px; border-radius:999px; color:${BODY};
  background:#fff; border:1px solid #D4D4D4; transition:border-color .2s, box-shadow .2s; }
.co-search:hover{ border-color:${INK}; }
.co-search:focus-within{ border-color:${INK}; box-shadow:0 0 0 3px rgba(0,0,0,.08); }
.co-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:15px; font-weight:400; letter-spacing:.04em; color:${INK}; height:100%; }
.co-search input::placeholder{ color:#A3A3A3; }
.co-pills{ display:flex; gap:8px; overflow-x:auto; padding:2px 2px 6px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.co-pills::-webkit-scrollbar{ display:none; }
.co-pill{ flex-shrink:0; display:inline-flex; align-items:center; gap:8px; height:40px; padding:0 18px; border-radius:999px; white-space:nowrap;
  background:#fff; border:1px solid ${INK} !important; font-size:11px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${INK}; transition:all .2s; }
.co-pill:hover{ border-color:${RED} !important; color:${RED}; }
.co-pill.on{ background:${INK}; color:#fff; }
.co-pill.on:hover{ background:${RED}; border-color:${RED} !important; color:#fff; }
.co-n{ min-width:22px; height:20px; padding:0 6px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; font-size:11px; font-weight:500; letter-spacing:0; background:#F0F0F0; color:${INK}; }
.co-pill.on .co-n{ background:#fff; color:${INK}; }

.co-empty{ display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:44px 24px; color:${INK}; }
.co-empty b{ font-family:'Cormorant Garamond',serif; font-size:28px; font-weight:500; }
.co-empty span{ font-size:14px; max-width:300px; line-height:22px; }

.co-card{ overflow:hidden; transition:border-color .2s, transform .2s; }
.co-card:hover{ border-color:${INK}; transform:translateY(-1px); }
.co-card.open{ border-color:${INK}; }
.co-card.faded{ opacity:.75; }
.co-head{ display:flex; align-items:center; gap:14px; width:100%; padding:14px 16px; text-align:left; }
.co-thumb{ width:60px; height:60px; border-radius:14px; overflow:hidden; flex-shrink:0; display:flex; align-items:center; justify-content:center;
  background:#F4F4F4; border:1px solid ${LINE}; color:#9A9A9A; }
.co-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.co-main{ flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
.co-title{ font-size:16px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.co-title em{ font-style:normal; font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:${RED}; margin-left:8px; }
.co-sub{ display:flex; align-items:center; gap:5px; font-size:12.5px; color:${BODY}; font-weight:400; letter-spacing:.03em; flex-wrap:wrap; }
.co-side{ display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
.co-side > b{ font-family:'Cormorant Garamond',serif; font-size:24px; font-weight:500; line-height:1; }
.co-chev{ color:${BODY}; transition:transform .2s, color .2s; }
.co-head:hover .co-chev{ color:${RED}; }
.co-card.open .co-chev{ transform:rotate(180deg); }

.co-badge{ display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:4px 10px; border-radius:999px; white-space:nowrap; border:1px solid transparent; }
.co-pending{ background:#fff; border-color:#D4D4D4; color:${BODY}; }
.co-packing{ background:${RED}; border-color:${RED}; color:#fff; }
.co-shipped{ background:${INK}; border-color:${INK}; color:#fff; }
.co-delivered{ background:#fff; border-color:${INK}; color:${INK}; }
.co-cancelled{ background:#F4F4F4; border-color:#F4F4F4; color:#8A8A8A; }
.co-refunded{ background:#fff; border-color:${RED}; color:${RED}; }

.co-body{ padding:4px 16px 16px; border-top:1px solid ${LINE}; display:flex; flex-direction:column; gap:12px; animation:coOpen .2s ease-out; }
@keyframes coOpen{ from{ opacity:0; transform:translateY(-6px); } to{ opacity:1; transform:translateY(0); } }

/* delivery tracker */
.co-track{ display:flex; align-items:flex-start; padding:16px 6px 4px; }
.co-step{ flex:1; display:flex; flex-direction:column; align-items:center; gap:8px; position:relative; font-size:10.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:#A3A3A3; text-align:center; }
.co-step::before{ content:""; position:absolute; top:13px; right:50%; width:100%; height:2px; background:${LINE}; z-index:0; }
.co-step:first-child::before{ display:none; }
.co-dot{ position:relative; z-index:1; width:28px; height:28px; border-radius:50%; background:#fff; border:2px solid ${LINE}; display:flex; align-items:center; justify-content:center; color:#fff; }
.co-step.done{ color:${INK}; }
.co-step.done::before{ background:${INK}; }
.co-step.done .co-dot{ background:${INK}; border-color:${INK}; }

.co-items{ display:flex; flex-direction:column; gap:10px; }
.co-item{ display:grid; grid-template-columns:48px 1fr auto; grid-template-areas:"photo name total" "photo qty total"; column-gap:12px; row-gap:1px; align-items:center;
  padding:8px; border-radius:14px; background:#fff; border:1px solid ${LINE}; }
.co-iphoto{ grid-area:photo; width:48px; height:48px; border-radius:12px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:#F4F4F4; color:#9A9A9A; }
.co-iphoto img{ width:100%; height:100%; object-fit:cover; display:block; }
.co-iname{ grid-area:name; font-size:14.5px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.co-item > .co-muted{ grid-area:qty; font-size:12.5px; }
.co-item > b{ grid-area:total; font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:500; }
.co-sum{ display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-radius:14px; background:#F7F7F7; border:1px solid ${LINE};
  font-size:11.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; }
.co-sum b{ font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:500; letter-spacing:0; text-transform:none; color:${INK}; }
.co-banner{ display:flex; align-items:center; gap:10px; padding:12px 14px; border-radius:14px; background:#fff; border:1px solid ${INK}; color:${INK}; font-size:13.5px; font-weight:400; letter-spacing:.02em; }
.co-banner.wait{ border-color:#D4D4D4; color:${BODY}; }
.co-actions{ display:flex; flex-wrap:wrap; align-items:center; gap:8px; }
.co-note{ display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; color:${INK}; }

@media (max-width:480px){
  .co-head{ padding:12px; gap:10px; }
  .co-thumb{ width:52px; height:52px; border-radius:12px; }
  .co-title{ font-size:15px; }
  .co-body{ padding:4px 12px 14px; }
}
@media (prefers-reduced-motion: reduce){ .co-page *{ transition:none !important; animation:none !important; } }
`;

type SK = "Pending" | "Packing" | "Shipped" | "Delivered" | "Cancelled" | "Refunded";

const STATUS_ICON: Record<SK, React.ReactNode> = {
  Pending: <Clock size={12} />,
  Packing: <Package size={12} />,
  Shipped: <Truck size={12} />,
  Delivered: <CheckCircle2 size={12} />,
  Cancelled: <Ban size={12} />,
  Refunded: <RotateCcw size={12} />,
};

const statusOf = (o: Order): SK => {
  if (o.isRefunded) return "Refunded";
  if (o.isCancelled) return "Cancelled";
  if (o.isDelivered || o.deliveryStatus?.buyerReceived) return "Delivered";
  if (o.deliveryStatus?.sellerDelivered || o.status === "shipped") return "Shipped";
  if (o.isPaid) return "Packing";
  return "Pending";
};

const money = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const when = (ms?: number | null) =>
  ms ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-";

type Filter = "active" | "Delivered" | "Cancelled" | "all";

export default function CustomerOrders() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { orders, status, error, actionLoading } = useSelector((s: RootState) => s.orders);

  const [filter, setFilter] = useState<Filter>("active");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const unsub = dispatch(listenToUserOrders());
    return () => unsub();
  }, [dispatch]);

  const count = (f: Filter) =>
    orders.filter((o) => {
      const s = statusOf(o);
      if (f === "all") return true;
      if (f === "active") return s === "Pending" || s === "Packing" || s === "Shipped";
      if (f === "Cancelled") return s === "Cancelled" || s === "Refunded";
      return s === f;
    }).length;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const s = statusOf(o);
      if (filter === "active" && !(s === "Pending" || s === "Packing" || s === "Shipped")) return false;
      if (filter === "Delivered" && s !== "Delivered") return false;
      if (filter === "Cancelled" && s !== "Cancelled" && s !== "Refunded") return false;
      if (!q) return true;
      return `${o.orderId} ${o.items.map((i) => i.name).join(" ")}`.toLowerCase().includes(q);
    });
  }, [orders, filter, search]);

  /* buyer half of delivery: only after the seller shipped (the slice enforces it too) */
  const received = (o: Order) => {
    if (window.confirm("Mark this order as received? Only do this once your parcel has arrived."))
      dispatch(markOrderDelivered({ customerId: o.customerId, orderId: o.orderId, role: "customer" }));
  };
  const cancel = (o: Order) => {
    if (window.confirm("Cancel this order?")) dispatch(cancelOrder({ customerId: o.customerId, orderId: o.orderId }));
  };
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(text); setTimeout(() => setCopied(""), 1200); } catch { /* blocked */ }
  };

  const pills: { id: Filter; label: string; icon: React.ReactNode }[] = [
    { id: "active", label: "Active", icon: <Package size={13} /> },
    { id: "Delivered", label: "Delivered", icon: <CheckCircle2 size={13} /> },
    { id: "Cancelled", label: "Cancelled", icon: <Ban size={13} /> },
    { id: "all", label: "All", icon: <Clock size={13} /> },
  ];

  return (
    <div className="co-page">
      <style>{CSS}</style>

      <div className="co-wrap">
        <button type="button" className="co-back" onClick={() => navigate("/")}><ArrowLeft size={16} /> Back</button>
        <h2 style={{ marginBottom: 4 }}>My orders</h2>

        <label className="co-search">
          <Search size={16} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or order no." />
        </label>

        <div className="co-pills">
          {pills.map((p) => (
            <button key={p.id} type="button" className={`co-pill${filter === p.id ? " on" : ""}`} onClick={() => setFilter(p.id)}>
              {p.icon} {p.label} <span className="co-n">{count(p.id)}</span>
            </button>
          ))}
        </div>

        {error && <p className="co-error">{error}</p>}

        {shown.length === 0 && status !== "failed" && (
          <div className="co-glass co-empty">
            <Package size={36} strokeWidth={1.4} />
            <b>{orders.length === 0 ? "No orders yet" : "No orders match"}</b>
            <span className="co-muted">
              {orders.length === 0 ? "When you place an order, it shows up here." : "Clear the search or pick another filter."}
            </span>
          </div>
        )}

        {shown.map((o) => {
          const s = statusOf(o);
          const open = openId === o.orderId;
          const first = o.items[0];
          const sellerShipped = o.deliveryStatus?.sellerDelivered === true || o.status === "shipped";
          const live = !o.isCancelled && !o.isRefunded && !o.isDelivered && !o.deliveryStatus?.buyerReceived;
          const canReceive = live && sellerShipped;
          const canCancel = live && !sellerShipped;
          const qty = o.items.reduce((n, i) => n + i.quantity, 0);
          const ended = s === "Cancelled" || s === "Refunded";
          const paid = o.isPaid;
          const shippedDone = sellerShipped || s === "Delivered";
          const receivedDone = s === "Delivered";
          return (
            <div key={o.orderId} className={`co-glass co-card${open ? " open" : ""}${ended ? " faded" : ""}`}>
              <button type="button" className="co-head" onClick={() => setOpenId(open ? null : o.orderId)} aria-expanded={open}>
                <span className="co-thumb">{first?.photo ? <img src={first.photo} alt="" /> : <Package size={22} strokeWidth={1.5} />}</span>
                <span className="co-main">
                  <b className="co-title">
                    {first?.name || "Order"}
                    {o.items.length > 1 && <em>+{o.items.length - 1} more</em>}
                  </b>
                  <span className="co-sub">#{o.orderId.slice(0, 8).toUpperCase()} · {when(o.createdAt)} · {qty} item{qty === 1 ? "" : "s"}</span>
                </span>
                <span className="co-side">
                  <span className={`co-badge co-${s.toLowerCase()}`}>{STATUS_ICON[s]} {s}</span>
                  <b>€{money(o.total)}</b>
                  <ChevronDown size={16} className="co-chev" />
                </span>
              </button>

              {open && (
                <div className="co-body">
                  {!ended && (
                    <div className="co-track" aria-label="Delivery progress">
                      <div className={`co-step${paid ? " done" : ""}`}><span className="co-dot">{paid && <Check size={14} strokeWidth={3.2} />}</span>Paid</div>
                      <div className={`co-step${shippedDone ? " done" : ""}`}><span className="co-dot">{shippedDone && <Truck size={13} strokeWidth={2.8} />}</span>Shipped</div>
                      <div className={`co-step${receivedDone ? " done" : ""}`}><span className="co-dot">{receivedDone && <PackageCheck size={14} strokeWidth={2.8} />}</span>Received</div>
                    </div>
                  )}

                  {canReceive && (
                    <div className="co-banner"><Truck size={18} /> Your order is on its way. Tap "Mark as received" when it arrives.</div>
                  )}
                  {canCancel && paid && (
                    <div className="co-banner wait"><Clock size={18} /> Waiting for the seller to ship. You can cancel until it ships.</div>
                  )}

                  <div className="co-items">
                    {o.items.map((i, n) => (
                      <div key={i.productId + n} className="co-item">
                        <span className="co-iphoto">{i.photo ? <img src={i.photo} alt="" /> : <Package size={18} strokeWidth={1.5} />}</span>
                        <span className="co-iname">{i.name}</span>
                        <span className="co-muted">{i.quantity} × €{money(i.price)}</span>
                        <b>€{money(i.price * i.quantity)}</b>
                      </div>
                    ))}
                  </div>

                  <div className="co-sum"><span>Total</span><b>€{money(o.total)}</b></div>
                  {o.paymentProvider && <p className="co-sub">Paid with {o.paymentProvider}{o.currency ? ` (${String(o.currency).toUpperCase()})` : ""}</p>}
                  {o.cancelReason && <p className="co-sub">Reason: {o.cancelReason}</p>}
                  {o.refundReason && <p className="co-sub">Refund note: {o.refundReason}</p>}

                  <div className="co-actions">
                    {canReceive && (
                      <button type="button" className="co-btn" disabled={actionLoading} onClick={() => received(o)}>
                        <PackageCheck size={15} /> Mark as received
                      </button>
                    )}
                    {s === "Delivered" && <span className="co-note"><CheckCircle2 size={14} /> You received this order</span>}
                    {canCancel && (
                      <button type="button" className="co-btn ghost" disabled={actionLoading} onClick={() => cancel(o)}>
                        <Ban size={15} /> Cancel order
                      </button>
                    )}
                    <button type="button" className="co-btn ghost" onClick={() => copy(o.orderId)}>
                      {copied === o.orderId ? <Check size={15} /> : <Copy size={15} />} Order ID
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}